<script setup>
import { onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';

import IntersectionObserver from 'components/IntersectionObserver.vue';
import Spinner from 'next/spinner/Spinner.vue';
import AuditTrail from './AuditTrail.vue';

const props = defineProps({
  filters: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();

const uiFlags = useMapGetter('wgptAuditTrail/getUIFlags');
const auditTrails = ref([]);

const currentPage = ref(null);
const hasMore = ref(false);

const infiniteLoaderOptions = {
  root: null,
  rootMargin: '50px 0px 50px 0px',
};

const fetchAuditTrails = async (page = 1) => {
  try {
    const commonFetchParams = {
      page,
      sortAttr: '-created_at',
    };
    const transformedFilters = props.filters;
    const { payload, meta } = await store.dispatch('wgptAuditTrail/get', {
      ...commonFetchParams,
      filters: transformedFilters,
    });
    auditTrails.value = auditTrails.value.concat(payload);
    const totalPages = Math.ceil(meta.total / 15);
    hasMore.value = meta.page < totalPages;
    currentPage.value = meta.page;
  } catch (error) {
    useAlert(error.message);
  }
};

const loadMoreAuditTrails = () => {
  fetchAuditTrails(currentPage.value + 1);
};

onMounted(() => {
  fetchAuditTrails();
  store.dispatch('wgptAuditTrail/clearFilters');
});
</script>

<template>
  <div>
    <div v-if="auditTrails.length" class="flex flex-col gap-3">
      <AuditTrail
        v-for="auditTrail in auditTrails"
        :key="auditTrail.id"
        :record="auditTrail"
      />

      <IntersectionObserver
        v-if="hasMore && !uiFlags.isFetching"
        :options="infiniteLoaderOptions"
        @observed="loadMoreAuditTrails"
      />
    </div>

    <div v-if="uiFlags.isFetching" class="text-center">
      <Spinner class="my-4 mx-auto text-n-slate-10" />
    </div>
  </div>
</template>
